import { ChartOfAccountsService } from '../../../../../services/accounting/chart-of-accounts.service';
import { Component, Inject, OnInit } from '@angular/core';
import { Equity } from '../../../../../models/entities/accounting/equity';
import { FixedAsset } from '../../../../../models/entities/accounting/fixed-asset';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { GenericDialogResult } from '../../../shared/generic-mat-dialog/generic-dialog-result';
import { GrowlerService } from 'app/services/growler.service';
import { Liability } from '../../../../../models/entities/accounting/liability';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import moment from 'moment';

export interface AccountEditorDialogInput
{
    create: boolean;
    organizationId: number;
    account?: FixedAsset | Liability | Equity;
}

@Component({
    selector: 'app-account-editor-dialog',
    templateUrl: './account-editor-dialog.component.html',
})
export class AccountEditorDialogComponent implements OnInit
{

    public title = '';
    public contextNoun = '';
    public submitInFlight = false;

    public form: UntypedFormGroup = null;

    constructor(
            private _chartOfAccountsService: ChartOfAccountsService,
            private _dialogRef: MatDialogRef<AccountEditorDialogComponent>,
            private _growler: GrowlerService,
            private _formBuilder: UntypedFormBuilder,
            @Inject(MAT_DIALOG_DATA) public data: AccountEditorDialogInput,
    ) {
    }

    ngOnInit() {
        this.form = this._formBuilder.group({
            label: [
                this.data.account?.label || '',
                Validators.required,
            ],
            startingBalance: [
                this.data.account?.startingBalance || 0,
                Validators.required,
            ],
            asOf: [
                this.data.account?.startingBalanceDate || null,
                Validators.required,
            ],
        });

        if (this.data.account instanceof FixedAsset && !this.data.account.owner && !this.data.account.internalUse) {
            this.form.addControl('useLikeBankAccount', this._formBuilder.control(false));
        }

        switch (this.data.account.constructor) {
            case FixedAsset:
                this.contextNoun = 'Asset';
                break;
            case Liability:
                this.contextNoun = 'Liability';
                break;
            case Equity:
                this.contextNoun = 'Equity';
                break;
        }

        this.title = this.data.create ? `Add ${this.contextNoun}` : `Update ${this.contextNoun}`;

    }

    closeDialog() {
        this._dialogRef.close(<GenericDialogResult>{success: false});
    }

    save() {
        if (!this.form.valid) {
            // Shouldn't be able to trigger save() with an invalid form, but just in case.
            this._growler.error('Missing fields', 'Please double check all required fields.');
            return;
        }

        const requestBody = this.form.getRawValue();
        if (moment.isMoment(requestBody.asOf)) {
            requestBody.asOf = requestBody.asOf.format('YYYY-MM-DD');
        }

        if (this.data.create) {
            let request = null;
            switch (this.data.account.constructor) {
                case FixedAsset:
                    request = this._chartOfAccountsService.createAsset(this.data.organizationId, requestBody);
                    break;
                case Liability:
                    request = this._chartOfAccountsService.createLiability(this.data.organizationId, requestBody);
                    break;
                case Equity:
                    request = this._chartOfAccountsService.createEquity(this.data.organizationId, requestBody);
                    break;
            }
            if (request !== null) {
                this.submitInFlight = true;
                request.subscribe(
                        () => {
                            this.submitInFlight = false;
                            this._growler.success('Success', this.contextNoun + ' added successfully');
                            this._dialogRef.close(<GenericDialogResult>{success: true});
                        },
                        (errorResponse) => {
                            this.submitInFlight = false;
                            this._growler.error(
                                    'Error',
                                    errorResponse.errors[0].msg,
                            );
                        },
                );
            }
        } else {
            let request = null;
            switch (this.data.account.constructor) {
                case FixedAsset:
                    request = this._chartOfAccountsService.updateAsset(null, this.data.account.id, requestBody);
                    break;
                case Liability:
                    request = this._chartOfAccountsService.updateLiability(null, this.data.account.id, requestBody);
                    break;
                case Equity:
                    request = this._chartOfAccountsService.updateEquity(null, this.data.account.id, requestBody);
                    break;
            }
            if (request !== null) {
                this.submitInFlight = true;
                request.subscribe(
                        () => {
                            this.submitInFlight = false;
                            this._growler.success('Success', this.contextNoun + ' updated successfully');
                            this._dialogRef.close(<GenericDialogResult>{success: true});
                        },
                        (errorResponse) => {
                            this.submitInFlight = false;
                            this._growler.error(
                                    'Error',
                                    errorResponse.errors[0].msg,
                            );
                        },
                );
            }
        }
    }
}
